import { useCallback, useEffect, useMemo, useState } from "react";
import {
	BoundingBox,
	boundingBoxContainsPoint,
	DeviceCategory,
	GET_ALL_DEVICES_BBOX_ALL,
	isDeviceLocated,
	isDevicePaired,
	PairedLocatedVelavuDevice,
	VelavuAPI,
	VelavuDevice,
} from "velavu-js-api";
import { applyDeviceUpdate, DeviceUpdate } from "../emitter/object-update";
import { useSubscribeCombinedDeviceUpdates } from "../emitter/stream-helper";

interface InternalMapDevicesState {
	activeRegion: BoundingBox | null;
	devices: PairedLocatedVelavuDevice[];
	isLoading: boolean;
	error: unknown | null;
	continuationToken: string | null;
	isComplete: boolean;
}

export interface MapDevicesState {
	activeRegion: BoundingBox | null;
	devices: PairedLocatedVelavuDevice[];
	isLoading: boolean;
	error: unknown | null;
	hasMore: boolean;
	isComplete: boolean;
}

function internalMapDevicesStateToPublicState(
	state: InternalMapDevicesState,
): MapDevicesState {
	return {
		activeRegion: state.activeRegion,
		devices: state.devices,
		isLoading: state.isLoading,
		error: state.error,
		hasMore: state.continuationToken !== null,
		isComplete: state.isComplete,
	};
}

export interface MapDevicesActions {
	loadRegion: (region: BoundingBox | null) => Promise<void>;
	loadMore: () => Promise<void>;
}

/**
 * Merges arrays of devices together, removing duplicates
 */
export function mergeDevices<T extends VelavuDevice>(...devices: T[]): T[] {
	const mergedDevicesMap = new Map<string, T>();
	for (const device of devices) {
		mergedDevicesMap.set(device.id, device);
	}
	return Array.from(mergedDevicesMap.values());
}

export function useMapDevicesState(): [MapDevicesState, MapDevicesActions] {
	const [devicesState, setDevicesState] = useState<InternalMapDevicesState>({
		activeRegion: null,
		devices: [],
		isLoading: false,
		error: null,
		continuationToken: null,
		isComplete: false,
	});

	const loadRegion = useCallback(
		async (region: BoundingBox | null) => {
			//Check to make sure we're not loading
			if (devicesState.isLoading) {
				console.warn("Attempted to load devices while already loading");
				return;
			}

			//Reset the devices state
			const newDevicesState: Readonly<InternalMapDevicesState> = {
				...devicesState,
				isLoading: true,
			};
			setDevicesState(newDevicesState);

			//Load devices within the region
			try {
				const allDevices = await VelavuAPI.devices.getAllDevices({
					category: DeviceCategory.Tag,
					paired: true,
					bbox: region ?? GET_ALL_DEVICES_BBOX_ALL,
				});

				//Retain devices that are still visible in the requested region
				let retainedDevices: PairedLocatedVelavuDevice[];
				if (region === null) {
					retainedDevices = [];
				} else {
					retainedDevices = devicesState.devices.filter((device) =>
						boundingBoxContainsPoint(
							region,
							device.location.coordinates,
						),
					);
				}
				const mergedDevices = mergeDevices<PairedLocatedVelavuDevice>(
					...retainedDevices,
					...allDevices.data,
				);

				setDevicesState({
					activeRegion: region,
					isLoading: false,
					devices: mergedDevices,
					continuationToken: allDevices.continuationToken ?? null,
					error: null,
					isComplete:
						region === null &&
						allDevices.continuationToken === undefined,
				});
			} catch (error) {
				setDevicesState({
					...newDevicesState,
					isLoading: false,
					error: error,
				});
			}
		},
		[devicesState, setDevicesState],
	);

	const loadMore = useCallback(async () => {
		//Check state
		if (devicesState.isLoading) {
			console.warn(
				"Attempted to load more devices while already loading",
			);
			return;
		}
		if (devicesState.continuationToken === null) {
			console.warn(
				"Attempted to load more devices without a continuation token",
			);
			return;
		}

		//Reset the devices state
		const newDevicesState: Readonly<InternalMapDevicesState> = {
			...devicesState,
			isLoading: true,
			error: null,
		};
		setDevicesState(newDevicesState);

		try {
			const moreDevices = await VelavuAPI.devices.getAllDevices(
				{
					category: DeviceCategory.Tag,
					paired: true,
					bbox: devicesState.activeRegion ?? GET_ALL_DEVICES_BBOX_ALL,
				},
				{
					continuationToken: devicesState.continuationToken,
				},
			);

			setDevicesState({
				...newDevicesState,
				isLoading: false,
				devices: mergeDevices(
					...newDevicesState.devices,
					...moreDevices.data,
				),
				continuationToken: moreDevices.continuationToken ?? null,
			});
		} catch (error) {
			setDevicesState({
				...newDevicesState,
				isLoading: false,
				error: error,
			});
		}
	}, [devicesState, setDevicesState]);

	useEffect(() => {
		//Load the initial region
		loadRegion(null);
	}, []); //eslint-disable-line react-hooks/exhaustive-deps

	//Subscribe to remote and locally emitted device updates
	useSubscribeCombinedDeviceUpdates(
		useCallback((update: DeviceUpdate) => {
			setDevicesState((devicesState) => {
				const devices = applyDeviceUpdate(
					update,
					devicesState.devices,
					(device): device is PairedLocatedVelavuDevice =>
						device.category === DeviceCategory.Tag &&
						isDeviceLocated(device) &&
						isDevicePaired(device) &&
						(devicesState.activeRegion === null ||
							boundingBoxContainsPoint(
								devicesState.activeRegion,
								device.location.coordinates,
							)),
				);

				if (devices === devicesState.devices) {
					return devicesState;
				}

				return {
					...devicesState,
					devices,
				};
			});
		}, []),
	);

	//Convert the internal state to public state
	const publicDevicesState = useMemo(() => {
		return internalMapDevicesStateToPublicState(devicesState);
	}, [devicesState]);

	//Memoize the actions
	const actions: MapDevicesActions = useMemo(() => {
		return {
			loadRegion,
			loadMore,
		};
	}, [loadRegion, loadMore]);

	return [publicDevicesState, actions];
}
